import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { camelCase } from 'lodash-es';
import {
  PostSaleLineItems,
  PostSaleLineItemsStatus,
  PostSaleOwnershipStatus
} from '../../../../../_shared/types/postSale';
import {
  StyledFooterWrapper,
  StyledStatus,
  StyledFooterModalLink,
  StyledFooterModalLinkIcon,
  StyledSecondaryButton
} from './Styles';

const useFootersPaymentsOnHold = ({
  trade,
  onClickDocumentOwning,
  onClickDocumentUpload
}) => {
  const [postSaleLineItemsStatuses, setPostSale] = useState({});
  const canDealerUploadDigitalTitle = trade?.canUploadDigitalTitle;
  useEffect(() => {
    const lineItems = trade?.onHoldStatuses || [];
    if (lineItems.length > 0) {
      const nextState = lineItems.reduce((acc, lineItem) => {
        if (lineItem.key === PostSaleLineItems.LIEN_RELEASE) {
          acc[PostSaleLineItems.LIEN_RELEASE] = lineItem.status;
        }
        if (lineItem.key === PostSaleLineItems.OWNERSHIP) {
          acc[PostSaleLineItems.OWNERSHIP] = lineItem.status;
        }
        return acc;
      }, {});
      setPostSale(nextState);
    }
  }, [trade?.onHoldStatuses]);

  const getTitleOwnershipButtonText = () => {
    switch (postSaleLineItemsStatuses[PostSaleLineItems.OWNERSHIP]) {
      case PostSaleOwnershipStatus.RECEIVED:
        return 'paymentsOnHold.uploadDocument.viewOwnership';
      case PostSaleOwnershipStatus.ERROR:
        return 'paymentsOnHold.uploadDocument.reviewOwnership';
      default:
        return 'paymentsOnHold.uploadDocument.uploadOwnership';
    }
  };

  const FooterByStatusKey = ({ statusKey }) => {
    try {
      switch (statusKey) {
        case PostSaleLineItems.LIEN_RELEASE:
          return (
            postSaleLineItemsStatuses[statusKey] === 0 && (
              <StyledFooterWrapper
                data-testid={`payments-on-hold-list-card-footer-lien-${trade?.id}`}
              >
                <StyledStatus>
                  <span className="title">
                    <FormattedMessage id="paymentsOnHold.lienRelease" />:
                  </span>
                  <FormattedMessage
                    id={`paymentsOnHold.${camelCase(
                      PostSaleLineItemsStatus[PostSaleLineItems.LIEN_RELEASE][
                        postSaleLineItemsStatuses[statusKey]
                      ]?.key
                    )}`}
                  />
                </StyledStatus>
                <StyledSecondaryButton
                  onClick={() => onClickDocumentUpload(trade, statusKey)}
                  data-testid={`payments-on-hold-list-card-footer-lien-${trade?.id}-documents-upload`}
                >
                  <FormattedMessage id="paymentsOnHold.uploadDocument.uploadLienRelease" />
                </StyledSecondaryButton>
              </StyledFooterWrapper>
            )
          );
        case PostSaleLineItems.OWNERSHIP:
          return (
            postSaleLineItemsStatuses[statusKey] !== null && (
              <StyledFooterWrapper
                data-testid={`payments-on-hold-list-card-footer-ownership-${trade?.id}`}
              >
                <StyledStatus>
                  <span className="title">
                    <FormattedMessage id="paymentsOnHold.ownership" />:
                  </span>
                  <FormattedMessage
                    id={`paymentsOnHold.${camelCase(
                      PostSaleLineItemsStatus[PostSaleLineItems.OWNERSHIP][
                        postSaleLineItemsStatuses[statusKey]
                      ]?.key
                    )}`}
                  />
                </StyledStatus>
                {postSaleLineItemsStatuses[statusKey] !==
                  PostSaleOwnershipStatus.APPROVED &&
                  (canDealerUploadDigitalTitle ? (
                    <StyledSecondaryButton
                      onClick={() => onClickDocumentUpload(trade, statusKey)}
                      data-testid={`payments-on-hold-list-card-footer-ownership-${trade?.id}-documents-upload`}
                    >
                      <FormattedMessage id={getTitleOwnershipButtonText()} />
                    </StyledSecondaryButton>
                  ) : (
                    <StyledFooterModalLink
                      onClick={() => onClickDocumentOwning(trade, statusKey)}
                      data-testid={`payments-on-hold-list-card-footer-ownership-${trade?.id}-documents-owining`}
                    >
                      <StyledFooterModalLinkIcon />
                      <FormattedMessage id="paymentsOnHold.ownership.neededForFunding" />
                    </StyledFooterModalLink>
                  ))}
              </StyledFooterWrapper>
            )
          );
        default:
          return null;
      }
    } catch (e) {
      return null;
    }
  };

  return Object.keys(postSaleLineItemsStatuses).reduce((acc, statusKey) => {
    const footer = FooterByStatusKey({ statusKey });
    if (React.isValidElement(footer)) {
      acc.push(footer);
    }
    return acc;
  }, []);
};

export default useFootersPaymentsOnHold;
